import { StatusEnum } from '../enums/statusEnum';

export const DocumentViewMode = {
  MY_DOCUMENTS: 'myDocuments',
  ALL_DOCUMENTS: 'allDocuments',
  PENDING_DOCUMENTS: 'pendingDocuments',
  UNASSIGNED_DOCUMENTS: 'unassignedDocuments',
  UNMATCHED_DOCUMENTS: 'unmatchedDocuments',
  ARCHIVE: 'archive',
};

const READY = StatusEnum.READY.value;
const PENDING = StatusEnum.PENDING.value;
const GENERATING = StatusEnum.GENERATING.value;
const FILED_AWAY = StatusEnum.FILED_AWAY.value;

/**
 * Assignee ids that count as "mine": the user, plus each team they belong to.
 * A colleague's user id does not count, even on a shared team.
 *
 * @param {string|null|undefined} userId
 * @param {{ teamId?: string, userIds?: string[] }[]|null|undefined} teams
 * @returns {Set<string>}
 */
export function myAssigneeIds(userId, teams) {
  const ids = new Set();
  if (userId) {
    ids.add(userId);
  }
  for (const team of teams || []) {
    if (team?.teamId && team.userIds?.includes(userId)) {
      ids.add(team.teamId);
    }
  }
  return ids;
}

/**
 * Whether a list row belongs in the view. Null means My Docs cannot tell yet
 * because team membership has not loaded and the assignee is not the user.
 *
 * @param {object|null|undefined} doc
 * @param {string} viewMode
 * @param {{ userId?: string|null, teams?: { teamId?: string, userIds?: string[] }[], teamsReady?: boolean }} context
 * @returns {boolean|null}
 */
export function documentBelongsInView(doc, viewMode, context = {}) {
  if (!doc) {
    return false;
  }

  const status = doc.status;
  const filedAway = status === FILED_AWAY;

  switch (viewMode) {
    case DocumentViewMode.ALL_DOCUMENTS:
      return !filedAway;
    case DocumentViewMode.ARCHIVE:
      return filedAway;
    case DocumentViewMode.PENDING_DOCUMENTS:
      return status === PENDING || status === GENERATING;
    case DocumentViewMode.UNASSIGNED_DOCUMENTS:
      return status === READY && !doc.assigneeId;
    case DocumentViewMode.UNMATCHED_DOCUMENTS:
      return status === READY && (!doc.nhsId || !doc.patientId);
    case DocumentViewMode.MY_DOCUMENTS:
      return belongsToMe(doc, filedAway, context);
    default:
      return belongsToMe(doc, filedAway, context);
  }
}

function belongsToMe(doc, filedAway, context) {
  if (filedAway || !doc.assigneeId) {
    return false;
  }
  if (doc.assigneeId === context.userId) {
    return true;
  }
  if (!context.teamsReady) {
    return null;
  }
  return myAssigneeIds(context.userId, context.teams).has(doc.assigneeId);
}

/**
 * Search matches NHS number, patient name, who filed the document away, and who
 * created it. A row already in the results is refreshed for any update. An id that
 * is not in the results is refreshed only when this transition can change whether
 * it matches.
 *
 * @param {boolean} isInResults
 * @param {{ from?: string, to?: string, type?: string, progress?: number }|null|undefined} transition
 * @returns {boolean}
 */
export function shouldRefreshSearch(isInResults, transition) {
  if (!transition || transition.to === 'NON_EXISTENT') {
    return false;
  }
  return isInResults || transitionMayChangeSearch(transition);
}

/**
 * @param {{ from?: string, to?: string, type?: string, progress?: number }|null|undefined} transition
 * @returns {boolean}
 */
export function transitionMayChangeSearch(transition) {
  if (!transition || transition.to === 'NON_EXISTENT') {
    return false;
  }

  const { from, to, type } = transition;
  const progress = Number(transition.progress);
  const processingFinished = from === GENERATING
    && (to === READY || to === 'SKIPPED')
    && (type === 'ALL' || progress >= 1);

  return (from === 'NON_EXISTENT' && to === PENDING)
    || (from === READY && to === PENDING)
    || to === FILED_AWAY
    || (from === 'QUEUED_FOR_FILING' && to === READY)
    || type === 'NHS_ID'
    || type === 'OWNING_PATIENT'
    || processingFinished;
}

/**
 * Fetch one row when it is already on screen, or when this transition can put it there.
 * Ids with a fetch in flight count as on screen, so a later event is not dropped
 * before that fetch lands.
 *
 * @param {boolean} isTracked
 * @param {{ from?: string, to?: string, type?: string, progress?: number }|null|undefined} transition
 * @param {string} viewMode
 * @returns {boolean}
 */
export function shouldFetchDocumentUpdate(isTracked, transition, viewMode) {
  return isTracked || transitionMayEnterView(transition, viewMode);
}

/**
 * An id that is not already on screen. True when this transition can add a row
 * to the view, so one list_data fetch is worth doing. The fetched row still has
 * to pass documentBelongsInView before it is inserted.
 *
 * Content steps report GENERATING → READY for one piece, with progress below 1
 * while the document is still pending. Those cannot enter Unassigned or Unmatched.
 * Type ALL, or progress of 1, means processing has finished. An ASSIGNEE edit
 * does not say whether the assignee was set or cleared.
 *
 * @param {{ from?: string, to?: string, type?: string, progress?: number }|null|undefined} transition
 * @param {string} viewMode
 * @returns {boolean}
 */
export function transitionMayEnterView(transition, viewMode) {
  if (!transition || transition.to === 'NON_EXISTENT') {
    return false;
  }

  const { from, to, type } = transition;
  const progress = Number(transition.progress);
  const processingFinished = from === GENERATING
    && (to === READY || to === 'SKIPPED')
    && (type === 'ALL' || progress >= 1);

  switch (viewMode) {
    case DocumentViewMode.ALL_DOCUMENTS:
      return from === 'NON_EXISTENT' && to === PENDING;
    case DocumentViewMode.PENDING_DOCUMENTS:
      return to === PENDING;
    case DocumentViewMode.ARCHIVE:
      return to === FILED_AWAY;
    case DocumentViewMode.UNASSIGNED_DOCUMENTS:
      return processingFinished
        || (from === 'EDITING' && to === READY && type === 'ASSIGNEE')
        || (from === 'QUEUED_FOR_FILING' && to === READY);
    case DocumentViewMode.UNMATCHED_DOCUMENTS:
      return processingFinished
        || (from === 'QUEUED_FOR_FILING' && to === READY);
    case DocumentViewMode.MY_DOCUMENTS:
      return from === 'EDITING' && to === READY && type === 'ASSIGNEE';
    default:
      return false;
  }
}

/**
 * Apply one fetched row to the list already on screen.
 * belongs false, or a missing document, removes it. belongs null keeps a row
 * that is already present and does not insert one that is not. belongs true
 * replaces or appends.
 *
 * @param {object[]|null|undefined} previous
 * @param {string} documentId
 * @param {object|null|undefined} fetched undefined means this id was aborted
 * @param {boolean|null} belongs
 * @returns {object[]}
 */
export function mergeFetchedDocument(previous, documentId, fetched, belongs) {
  if (!Array.isArray(previous)) {
    return previous;
  }

  const position = previous.findIndex(doc => doc.documentId === documentId);
  const without = () => previous.filter(doc => doc.documentId !== documentId);

  if (fetched == null || belongs === false) {
    return position === -1 ? previous : without();
  }

  if (belongs == null) {
    if (position === -1) {
      return previous;
    }
    const next = previous.slice();
    next[position] = fetched;
    return next;
  }

  if (position === -1) {
    return [...previous, fetched];
  }

  const next = previous.slice();
  next[position] = fetched;
  return next;
}
